/**
 * PATH: packages/bills/src/coaOptions.ts
 * FILE_ID: BILLS-PKG-COA-OPTIONS-001
 * PURPOSE: Pure helpers for the per-line allocation grid (p3b) — turn the
 *          flat CoA account list into grouped <optgroup> options, and pin
 *          the cost-category + tax-type vocabularies the grid offers.
 *
 * WHY: Keep the rendering component dumb. Grouping/labelling/sorting is
 *      pure (input → output), so it is unit-testable in isolation and the
 *      grid never has to reason about CoA shape.
 *
 * DEPENDENCIES: ../types only (CoaAccount). Zero runtime deps.
 */

import type { CoaAccount } from './types';

/**
 * Quotech cost-category buckets. Mirrors
 * ``report.XeroAccount.cost_category`` (materials / subcontractors /
 * plant_and_equipment / labour / other) so the operator's per-line coding
 * choice maps 1:1 onto the CFO cost rollups.
 */
export const COST_CATEGORY_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: '—' },
  { value: 'materials', label: 'Materials' },
  { value: 'subcontractors', label: 'Subcontractors' },
  { value: 'plant_and_equipment', label: 'Plant & Equipment' },
  { value: 'labour', label: 'Labour' },
  { value: 'other', label: 'Other' },
];

/**
 * AU GST tax types Xero accepts on an ACCPAY line. Matches the push
 * envelope's defaults (INPUT = GST on Expenses, BASEXCLUDED = no GST).
 * Kept short — the common AP cases — with a blank "default" sentinel.
 */
export const TAX_TYPE_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Default' },
  { value: 'INPUT', label: 'GST on Expenses (10%)' },
  { value: 'GSTONIMPORTS', label: 'GST on Imports' },
  { value: 'BASEXCLUDED', label: 'BAS Excluded' },
  { value: 'EXEMPTEXPENSES', label: 'GST Free Expenses' },
  { value: 'NONE', label: 'No Tax' },
];

export interface CoaOptionGroup {
  /** optgroup label, e.g. "Expenses › Cost of Sales". */
  label: string;
  options: Array<{ value: string; label: string; account: CoaAccount }>;
}

const _TYPE_TO_CLASS_LABEL: Record<string, string> = {
  BANK: 'Assets',
  CURRENT: 'Assets',
  FIXED: 'Assets',
  INVENTORY: 'Assets',
  NONCURRENT: 'Assets',
  PREPAYMENT: 'Assets',
  CURRLIAB: 'Liabilities',
  LIABILITY: 'Liabilities',
  TERMLIAB: 'Liabilities',
  PAYGLIABILITY: 'Liabilities',
  SUPERANNUATIONLIABILITY: 'Liabilities',
  EQUITY: 'Equity',
  REVENUE: 'Income',
  SALES: 'Income',
  OTHERINCOME: 'Income',
  EXPENSE: 'Expenses',
  DIRECTCOSTS: 'Expenses',
  OVERHEADS: 'Expenses',
  DEPRECIATN: 'Expenses',
  SUPERANNUATIONEXPENSE: 'Expenses',
  WAGESEXPENSE: 'Expenses',
};

// Reading order: balance sheet first, then P&L.
const _CLASS_ORDER = ['Assets', 'Liabilities', 'Equity', 'Income', 'Expenses', 'Other'];

function classLabel(account: CoaAccount): string {
  const t = (account.account_type || '').trim().toUpperCase();
  return _TYPE_TO_CLASS_LABEL[t] || 'Other';
}

function groupLabel(account: CoaAccount): string {
  const grp = (account.reporting_code_name || '').trim();
  if (grp) return grp;
  const td = (account.account_type_display || '').trim();
  return td || 'Ungrouped';
}

/** A human label for one account: "310 · Materials" (code optional). */
export function accountLabel(account: CoaAccount): string {
  const code = (account.code || '').trim();
  return code ? `${code} · ${account.name}` : account.name;
}

/**
 * Build grouped <optgroup> data for the CoA picker from a flat account
 * list. Only ACTIVE accounts are offered (archived codes can't be posted
 * to). The option ``value`` is the account ``code`` (what the GL line
 * needs); codeless accounts are dropped (they can't be posted).
 *
 * Groups are "Class › Group" and ordered balance-sheet → P&L, then
 * alphabetically within a class; accounts inside a group sort by code.
 */
export function buildCoaOptionGroups(accounts: CoaAccount[]): CoaOptionGroup[] {
  // class -> group -> accounts
  const byClass = new Map<string, Map<string, CoaAccount[]>>();
  for (const acc of accounts) {
    if (!acc || typeof acc !== 'object') continue;
    if (acc.status && acc.status.toUpperCase() !== 'ACTIVE') continue;
    if (!acc.code || !acc.code.trim()) continue; // can't post without a code
    const cls = classLabel(acc);
    const grp = groupLabel(acc);
    if (!byClass.has(cls)) byClass.set(cls, new Map());
    const groups = byClass.get(cls)!;
    if (!groups.has(grp)) groups.set(grp, []);
    groups.get(grp)!.push(acc);
  }

  const classes = [
    ..._CLASS_ORDER.filter((c) => byClass.has(c)),
    ...[...byClass.keys()].filter((c) => !_CLASS_ORDER.includes(c)).sort(),
  ];

  const out: CoaOptionGroup[] = [];
  for (const cls of classes) {
    const groups = byClass.get(cls)!;
    for (const grp of [...groups.keys()].sort()) {
      const accs = groups
        .get(grp)!
        .slice()
        .sort((a, b) => (a.code || '').localeCompare(b.code || '', undefined, { numeric: true }));
      out.push({
        label: cls === grp ? cls : `${cls} › ${grp}`,
        options: accs.map((account) => ({
          value: account.code as string,
          label: accountLabel(account),
          account,
        })),
      });
    }
  }
  return out;
}
